import { ClaimWithDetails, CommunicationLog } from "@/types";
import { 
  MessageSquare, CheckCircle, 
  ArrowRight, ArrowLeft, FolderUp 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import React from "react";
import { 
  ClaimStatus, 
  getStatusDisplayName,
  getStatusIndex,
  isTerminalStatus
} from "@/lib/claim-status-constants";

interface ClaimHistoryProps {
  claimData: ClaimWithDetails;
}

// Define types for our history events' metadata
interface StatusChangeMetadata {
  status: string;
  displayStatus: string;
  previousStatus: string | null;
  previousDisplayStatus?: string; 
  direction: 'forward' | 'backward' | 'initial';
  isTerminal: boolean;
}

interface DocumentUploadMetadata {
  documentType: string; 
  fileName: string;
}

interface CommunicationMetadata {
  logType: string;
  userId: string | null;
}

// Base event structure
interface BaseHistoryEvent {
  id: string;
  title: string;
  description: string;
  timestamp: Date;
}

// Discriminated union for HistoryEvent
interface StatusChangeEvent extends BaseHistoryEvent {
  type: 'status_change';
  metadata: StatusChangeMetadata;
}

interface DocumentUploadEvent extends BaseHistoryEvent {
  type: 'document_upload';
  metadata: DocumentUploadMetadata;
}

interface CommunicationEvent extends BaseHistoryEvent {
  type: 'communication';
  metadata: CommunicationMetadata;
}

type HistoryEvent = StatusChangeEvent | DocumentUploadEvent | CommunicationEvent;

export default function ClaimHistory({ claimData }: ClaimHistoryProps) {
  const claim = claimData.claim;
  
  // Create a single "received" event based on claim creation date
  const createInitialEvent = (): StatusChangeEvent => {
    return {
      id: `status-create-${claim.claimId}`,
      type: 'status_change',
      title: 'Claim Creation',
      description: `Claim ${claim.claimIdentifier} was received`,
      timestamp: new Date(claim.createdAt),
      metadata: {
        status: 'Acknowledgment',
        displayStatus: getStatusDisplayName('Acknowledgment'),
        previousStatus: null,
        direction: 'initial',
        isTerminal: false
      }
    };
  };
  
  // Convert document uploads to history events
  const documentEvents: DocumentUploadEvent[] = claimData.documents.map((doc) => ({
    id: doc.document.documentMetadataId,
    type: 'document_upload',
    title: 'Document Uploaded',
    description: `${doc.document.fileName} was uploaded`,
    timestamp: new Date(doc.document.createdAt),
    metadata: {
      documentType: doc.claimDocument.documentType,
      fileName: doc.document.fileName
    }
  }));
  
  // Convert communications to history events
  const communicationEvents: CommunicationEvent[] = claimData.communicationLogs.map((log: CommunicationLog) => ({
    id: log.logId,
    type: 'communication',
    title: log.subject || log.logType,
    description: log.body || "No message body provided", // Ensure description is always a string
    timestamp: new Date(log.timestamp),
    metadata: {
      logType: log.logType,
      userId: log.userId
    }
  }));
  
  // Get status changes as history events
  const getStatusChangeEvents = (): StatusChangeEvent[] => {
    const events: StatusChangeEvent[] = [];
    
    // Add initial creation event
    events.push(createInitialEvent());
    
    // If we have tracked status changes, use those
    if (claimData.statusChanges && claimData.statusChanges.length > 0) {
      // Convert tracked status changes to events
      const statusEvents = claimData.statusChanges.map((change): StatusChangeEvent => {
        // Determine direction of change using shared utility
        const prevIdx = getStatusIndex(change.previousStatus as ClaimStatus);
        const newIdx = getStatusIndex(change.newStatus as ClaimStatus);
        let direction: 'forward' | 'backward' | 'initial' = 'forward';
        if (prevIdx === -1 || newIdx === -1) direction = 'initial'; // Should not happen with proper sequence
        else if (newIdx < prevIdx) direction = 'backward';
        
        const isTerminal = isTerminalStatus(change.newStatus as ClaimStatus);
        
        return {
          id: `status-change-${change.changeId}`,
          type: 'status_change',
          title: `Status Changed to ${getStatusDisplayName(change.newStatus as ClaimStatus)}`,
          description: `Moved from ${getStatusDisplayName(change.previousStatus as ClaimStatus)} to ${getStatusDisplayName(change.newStatus as ClaimStatus)}`,
          timestamp: new Date(change.timestamp),
          metadata: {
            status: change.newStatus,
            displayStatus: getStatusDisplayName(change.newStatus as ClaimStatus),
            previousStatus: change.previousStatus,
            previousDisplayStatus: getStatusDisplayName(change.previousStatus as ClaimStatus),
            direction,
            isTerminal
          }
        };
      });
      
      return [...events, ...statusEvents];
    }
    
    // If no tracked status changes but we're not in initial state,
    // add a synthetic event for the current status
    if (claim.status !== 'Acknowledgment') {
      events.push({
        id: `status-current-${claim.claimId}`,
        type: 'status_change',
        title: `Current Status: ${getStatusDisplayName(claim.status as ClaimStatus)}`,
        description: `Status was changed to ${getStatusDisplayName(claim.status as ClaimStatus)}`,
        timestamp: new Date(new Date(claim.createdAt).getTime() + 60000), // 1 minute after creation
        metadata: {
          status: claim.status,
          displayStatus: getStatusDisplayName(claim.status as ClaimStatus),
          previousStatus: 'Acknowledgment',
          previousDisplayStatus: getStatusDisplayName('Acknowledgment'),
          direction: 'forward',
          isTerminal: isTerminalStatus(claim.status as ClaimStatus)
        }
      });
    }
    
    return events;
  };
  
  // Get all status change events
  const statusChangeEvents = getStatusChangeEvents();
  
  // Combine all events and sort by timestamp (oldest first)
  const allEvents: HistoryEvent[] = [
    ...statusChangeEvents,
    ...documentEvents,
    ...communicationEvents
  ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Claim History</h3>
      
      {allEvents.length > 0 ? (
        <div className="relative">
          {/* Timeline */}
          <div className="absolute left-7 top-0 bottom-0 w-px bg-border z-0"></div>
          
          {/* Events */}
          <div className="space-y-8 relative z-10">
            {allEvents.map((event) => (
              <div key={event.id} className="flex">
                <div className="flex-shrink-0 mr-4">
                  <div className={`h-14 w-14 rounded-full flex items-center justify-center 
                    ${event.type === 'status_change' 
                      ? event.metadata.direction === 'backward'
                        ? 'bg-amber-50 text-amber-600'
                        : event.metadata.isTerminal
                          ? 'bg-green-50 text-green-600'
                          : event.metadata.status === 'Creation' || event.metadata.direction === 'initial'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-blue-50 text-blue-600'
                      : event.type === 'document_upload' 
                        ? 'bg-teal-50 text-teal-600'
                        : 'bg-purple-50 text-purple-600'}`}>
                    {event.type === 'status_change' && (
                      event.metadata.isTerminal 
                        ? <CheckCircle className="h-6 w-6" /> 
                        : event.metadata.direction === 'backward'
                          ? <ArrowLeft className="h-6 w-6" />
                          : <ArrowRight className="h-6 w-6" />
                    )}
                    {event.type === 'document_upload' && <FolderUp className="h-6 w-6" />}
                    {event.type === 'communication' && <MessageSquare className="h-6 w-6" />}
                  </div>
                </div>
                
                <div className="bg-card border rounded-lg p-4 flex-grow shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium">{event.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {/* Show date with time */}
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        event.type === 'status_change' 
                          ? event.metadata.direction === 'backward'
                            ? 'destructive'
                            : 'default'
                          : event.type === 'document_upload' 
                            ? 'outline' 
                            : 'secondary'
                      }
                      className={`ml-2 ${event.type === 'status_change' && event.metadata.isTerminal ? 'bg-green-100 text-green-800 hover:bg-green-200' : ''}`}
                    >
                      {event.type === 'status_change'
                        ? 'Status Change'
                        : event.type === 'document_upload'
                          ? 'Document'
                          : 'Communication'}
                    </Badge>
                  </div>
                  
                  <div className="text-sm mt-1">
                    {event.type === 'communication' ? (
                      <div className="mt-2 bg-muted/10 p-3 rounded whitespace-pre-line">
                        {event.description}
                      </div>
                    ) : (
                      <p>{event.description}</p>
                    )}
                    
                    {event.type === 'document_upload' && event.metadata.documentType && (
                      <span className="text-xs px-2 py-1 bg-muted rounded-full mt-1 inline-block">
                        {event.metadata.documentType}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 border rounded-lg">
          <p className="text-muted-foreground">No history events found for this claim.</p>
        </div>
      )}
    </div>
  );
}